import { Button, Input } from '@you/ui'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useRegister } from '@/api/hooks/useUser'

interface RegisterFormProps {
  onSwitchToLogin: () => void
  idPrefix?: string
}

export const RegisterForm = ({
  onSwitchToLogin,
  idPrefix = '',
}: RegisterFormProps) => {
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [errors, setErrors] = useState<{
    name?: string
    password?: string
    phone?: string
    verificationCode?: string
    api?: string
  }>({})

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    trigger: register,
    isMutating: isRegistering,
    // error: registerError,
  } = useRegister()

  const handleSendCode = () => {
    if (!phone) {
      setErrors((prev) => ({ ...prev, phone: '请输入手机号' }))
      return
    }
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setErrors((prev) => ({ ...prev, phone: '请输入正确的手机号' }))
      return
    }

    setErrors({})
    // TODO: 发送验证码逻辑
    console.log('发送验证码到:', phone)
    setCountdown(60)
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: {
      name?: string
      password?: string
      phone?: string
      verificationCode?: string
    } = {}

    if (!name) newErrors.name = '请输入用户名'
    if (!password) newErrors.password = '请输入密码'
    if (!phone) {
      newErrors.phone = '请输入手机号'
    } else if (!/^1[3-9]\d{9}$/.test(phone)) {
      newErrors.phone = '请输入正确的手机号'
    }
    if (!verificationCode) {
      newErrors.verificationCode = '请输入验证码'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      await register({
        name,
        password,
        mobile: phone,
        captcha: verificationCode,
      })

      // 检查是否有重定向参数
      const redirectUrl = searchParams.get('redirect')
      if (redirectUrl) {
        console.log('注册成功，重定向到:', redirectUrl)
        // 使用window.location.href进行重定向，因为可能包含查询参数
        window.location.href = decodeURIComponent(redirectUrl)
      } else {
        // 没有重定向参数，跳转到首页
        navigate('/')
      }
    } catch (e) {
      console.error('Registration failed', e)
    }
  }

  // 检查表单是否可以提交
  const isFormValid =
    name.trim() !== '' &&
    password.trim() !== '' &&
    phone.trim() !== '' &&
    verificationCode.trim() !== ''

  return (
    <div className="w-full">
      <div className="flex items-center gap-3 mb-8">
        <button
          onClick={onSwitchToLogin}
          className="text-[24px] text-[#0B0B0B] hover:opacity-80 hover:-translate-x-1 transition-all duration-200"
        >
          ←
        </button>
        <span className="text-[24px] font-medium text-[#0B0B0B]">
          注册
          <span className="text-[24px] font-medium bg-gradient-to-r from-[#0059FF] to-[#8752FF] bg-clip-text text-transparent">
            YouKnow
          </span>
        </span>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor={`${idPrefix}register-name`} className="sr-only">
              用户名
            </label>
            <Input
              id={`${idPrefix}register-name`}
              name="name"
              type="text"
              autoComplete="name"
              placeholder="请输入用户名"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (errors.name) setErrors((p) => ({ ...p, name: undefined }))
              }}
              error={!!errors.name}
              errorMessage={errors.name}
            />
          </div>
          <div>
            <label htmlFor={`${idPrefix}register-password`} className="sr-only">
              密码
            </label>
            <Input
              id={`${idPrefix}register-password`}
              name="password"
              type="password"
              autoComplete="new-password"
              placeholder="请输入密码"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                if (errors.password)
                  setErrors((p) => ({ ...p, password: undefined }))
              }}
              error={!!errors.password}
              errorMessage={errors.password}
            />
          </div>
          <div>
            <label htmlFor={`${idPrefix}register-phone`} className="sr-only">
              手机号
            </label>
            <Input
              id={`${idPrefix}register-phone`}
              name="phone"
              type="tel"
              autoComplete="tel"
              placeholder="请输入手机号"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value)
                if (errors.phone) {
                  setErrors((prev) => ({ ...prev, phone: undefined }))
                }
              }}
              error={!!errors.phone}
              errorMessage={errors.phone}
            />
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label
                htmlFor={`${idPrefix}register-verificationCode`}
                className="sr-only"
              >
                验证码
              </label>
              <Input
                id={`${idPrefix}register-verificationCode`}
                name="verificationCode"
                type="text"
                placeholder="请输入验证码"
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value)
                  if (errors.verificationCode) {
                    setErrors((prev) => ({
                      ...prev,
                      verificationCode: undefined,
                    }))
                  }
                }}
                error={!!errors.verificationCode}
                errorMessage={errors.verificationCode}
              />
            </div>
            <Button
              type="button"
              className="w-[120px] h-[36px] text-[14px] rounded-[99px] bg-gradient-primary disabled:opacity-50"
              onClick={handleSendCode}
              disabled={countdown > 0}
            >
              {countdown > 0 ? `${countdown}秒后重试` : '发送验证码'}
            </Button>
          </div>
          {/* {registerError && (
            <p className="text-red-500 text-sm text-center">
              {registerError.message || '注册失败，请重试'}
            </p>
          )} */}
        </div>

        <div className="flex flex-col items-center gap-4">
          <Button
            className="w-[298px] h-[50px] text-[16px] rounded-[99px] bg-gradient-primary"
            type="submit"
            disabled={!isFormValid || isRegistering}
          >
            {isRegistering ? '注册中...' : '注册'}
          </Button>
          <button
            className="text-primary text-[14px] relative group"
            type="button"
            onClick={onSwitchToLogin}
          >
            <span>已有账号，直接登录</span>
            <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-gradient-to-r from-[#0059FF] to-[#8752FF] transition-all duration-300 group-hover:w-full"></span>
          </button>
        </div>
      </form>
    </div>
  )
}
